import classNames from 'classnames'
import _ from 'lodash'
import { Loader } from '@navikt/ds-react'
import PT from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import DashboardGrid from './DashboardGrid'
import styled, { createGlobalStyle } from 'styled-components'
import DashboardConfig from 'config/DashboardConfig'
import defaultLabels from './Dashboard.labels'
import { Layout as RGLLayout } from 'react-grid-layout'
import {
  BreakpointPropType,
  ConfigPropType,
  LabelsPropType,
  LayoutTabsPropType,
  WidgetMapPropType,
  WidgetsPropType
} from 'index.pt'
import * as DashboardAPI from './DashboardAPI'
import DashboardControlPanel from './DashboardControlPanel'
import {
  Breakpoint,
  Config,
  DashboardProps,
  DroppedItem,
  DroppingItem,
  Labels,
  Layout,
  LayoutBody,
  Layouts,
  LayoutTab,
  LayoutTabs,
  Widget,
  WidgetMap,
  WidgetPlaceholder,
  Widgets,
  WidgetTemplates
} from 'index.d'
import WidgetAddArea from './WidgetAddArea'
import * as DashboardDefaultWidgets from 'widgets'
import 'react-grid-layout/css/styles.css'

const DashboardDiv = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  .columns {
    columns: 120px;
  }
`
const GlobalStyle = createGlobalStyle` 
  .ekspanderbartPanel__heading {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`
const WidgetAddAreaDiv = styled.div`
  transition: all .5s ease-in-out;
  height: 0;
  &.open {
    height: 7rem;
  }
`
const Dashboard: React.FC<DashboardProps> = ({
  afterLayoutChange, allowedWidgets = undefined, className, defaultConfig, defaultLayouts, defaultTab ='default', defaultWidgets,
  extraWidgets, highContrast = false, id, initialBreakpoint = 'lg', labels, configurable = true
}: DashboardProps): JSX.Element => {
  const [addMode, setAddMode] = useState<boolean>(false)
  const [availableWidgets, setAvailableWidgets] = useState<WidgetTemplates>([])
  const [backupLayouts, setBackupLayouts] = useState<LayoutTabs>([])
  const [config, setConfig] = useState<Config>({} as Config)
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(initialBreakpoint)
  const [currentTabKey, setCurrentTabKey] = useState<string>(defaultTab)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [fullFocusMode, setFullFocusMode] = useState<boolean>(false)
  const [layouts, _setLayouts] = useState<LayoutTabs>([])
  const [mounted, setMounted] = useState<boolean>(false)
  const [widgets, setWidgets] = useState<Widgets>([])
  const _labels: Labels = { ...defaultLabels, ...labels }
  const [droppingItem, setDroppingItem] = useState<DroppingItem>({ i: '', w: 0, h: 0 })
  const [draggedWidget, setDraggedWidget] = useState<any>(undefined)
  const myWidgets: WidgetMap<any> = { ...extraWidgets, ...(DashboardDefaultWidgets as WidgetMap<any>) }
  const droppingItemId: string = '__dropping-elem__'

  const initDashboard = useCallback(async () => {
    const _availableWidgets: WidgetTemplates = DashboardAPI.loadAvailableWidgets(myWidgets, allowedWidgets)
    setAvailableWidgets(_availableWidgets)
    const [_widgets, _layouts, _config]: [Widgets, LayoutTabs, Config] =
      await DashboardAPI.loadDashboard(id, defaultWidgets, defaultLayouts, defaultConfig, allowedWidgets)
    setWidgets(_widgets)
    _setLayouts(_layouts)
    setConfig(_config)
    const winWidth = window.innerWidth;
    for (var key in _config.breakpoints) {
      if (winWidth > _config.breakpoints[key as Breakpoint]) {
        setCurrentBreakpoint(key as Breakpoint)
      }
    }
  }, [allowedWidgets, defaultConfig, defaultLayouts, defaultWidgets, id, myWidgets, _setLayouts])

  useEffect(() => {
    if (!mounted) {
      initDashboard()
      setMounted(true)
    }
  }, [mounted])

  const onWidgetFullFocus = (focusedWidget: Widget): void => {
    if (!fullFocusMode) {
      setFullFocusMode(true)
      const newWidgets: Widgets = widgets.map((widget: Widget) => ({
        ...widget,
        visible: focusedWidget.i === widget.i
      }))
      const newLayouts: LayoutTabs = _.cloneDeep(layouts)
      const currentTabIndex: number = _.findIndex(newLayouts, {key: currentTabKey})
      Object.keys(newLayouts[currentTabIndex].body).forEach(breakpoint => {
        newLayouts[currentTabIndex].body[breakpoint as Breakpoint] =
          newLayouts[currentTabIndex].body[breakpoint as Breakpoint].filter((layout: Layout) => {
            const widget: Widget = _.find(newWidgets, (w) => w.i === layout.i)!
            return widget.visible
          })
      })
      setWidgets(newWidgets)
      setBackupLayouts(layouts)
      setLayouts(newLayouts)
    }
  }

  const setLayouts = (layouts: LayoutTabs): void => {
    _setLayouts(layouts)
    if (_.isFunction(afterLayoutChange)) {
      afterLayoutChange(layouts)
    }
  }

  const onWidgetRestoreFocus = (): void => {
    if (fullFocusMode) {
      setFullFocusMode(false)
      setWidgets(widgets.map((widget: Widget) => ({
        ...widget,
        visible: true
      })))
      setLayouts(backupLayouts)
    }
  }

  const onWidgetUpdate = async (updatedWidget: Widget, layout: Layout): Promise<any> => {
    const newWidgets: Widgets = widgets.map((widget) => {
      return (widget.i === layout.i) ? updatedWidget : widget
    })
    setWidgets(newWidgets)
    await DashboardAPI.saveWidgets(id, newWidgets)
  }

  const onWidgetAdd = async (newWidget: Widget): Promise<any> => {
    const newWidgets: Widgets = widgets.concat(newWidget)
    setWidgets(newWidgets)
  }

  const onPlaceholderWidgetAdd = async (placeholderWidget: WidgetPlaceholder): Promise<any> => {
    placeholderWidget.i = droppingItemId
    setDroppingItem({
      i: droppingItemId,
      w: placeholderWidget.defaultLayout[currentBreakpoint].defaultW,
      h: placeholderWidget.defaultLayout[currentBreakpoint].defaultH
    } as DroppingItem)
    onWidgetAdd(placeholderWidget)
  }

  const onWidgetResize = (layout: Layout): void => {
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const currentTabIndex: number = _.findIndex(newLayout, {key: currentTabKey})
    const index: number = _.findIndex(newLayout[currentTabIndex].body[currentBreakpoint], { i: layout.i })
    newLayout[currentTabIndex].body[currentBreakpoint][index] = layout
    setLayouts(newLayout)
  }

  const onWidgetDelete = (layout: Layout): void => {
    setWidgets(_.reject(widgets, { i: layout.i }))
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const currentTabIndex: number = _.findIndex(newLayout, {key: currentTabKey})
    Object.keys(newLayout[currentTabIndex].body).forEach(breakpoint => {
      newLayout[currentTabIndex].body[breakpoint as Breakpoint] =
        _.reject(newLayout[currentTabIndex].body[breakpoint as Breakpoint], { i: layout.i })
    })
    setLayouts(newLayout)
  }

  const onWidgetDrop = async (elem: DroppedItem): Promise<any> => {
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const currentTabIndex: number = _.findIndex(newLayout, {key: currentTabKey})
    const newWidget: WidgetPlaceholder = (_.find(widgets, { i: droppingItemId }) as WidgetPlaceholder)
    if (newWidget) {
      newWidget.i = 'w-' + new Date().getTime()
      // replace placeholder with new widget
      const newWidgets = widgets.map((widget) => {
        return widget.i === droppingItemId
          ? {
              i: newWidget.i,
              type: newWidget.type,
              title: newWidget.title,
              visible: newWidget.visible,
              options: newWidget.options
            }
          : widget
      })
      // replace placeholder in layout with new widget layout, fill out non-active breakpoints
      Object.keys(newLayout[currentTabIndex].body).forEach(breakpoint => {
        const placeholderLayout = _.find(newLayout[currentTabIndex].body[breakpoint as Breakpoint],
          { i: droppingItemId })
        if (placeholderLayout) {
          newLayout[currentTabIndex].body[breakpoint as Breakpoint] =
            newLayout[currentTabIndex].body[breakpoint as Breakpoint].map(layout => {
              return layout.i === droppingItemId
                ? {
                    ...layout,
                    i: newWidget.i
                  }
                : layout
            })
        } else {
          newLayout[currentTabIndex].body[breakpoint as Breakpoint].push({
            i: newWidget.i,
            x: 0,
            y: 0,
            w: elem.w <= newWidget.defaultLayout[breakpoint as Breakpoint].maxW
              ? elem.w
              : newWidget.defaultLayout[breakpoint as Breakpoint].defaultW,
            h: elem.h <= newWidget.defaultLayout[breakpoint as Breakpoint].maxH
              ? elem.h
              : newWidget.defaultLayout[breakpoint as Breakpoint].defaultH,
            minW: newWidget.defaultLayout[breakpoint as Breakpoint].minW,
            minH: newWidget.defaultLayout[breakpoint as Breakpoint].minH,
            maxW: newWidget.defaultLayout[breakpoint as Breakpoint].maxW,
            maxH: newWidget.defaultLayout[breakpoint as Breakpoint].maxH
          })
        }
      })
      setWidgets(newWidgets)
      setLayouts(newLayout)
    }
  }

  const onLayoutChange = (changedLayout: Layouts, _layouts: LayoutBody): void => {
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const currentTabIndex: number = _.findIndex(newLayout, {key: currentTabKey})
    newLayout[currentTabIndex].body = _layouts
    setLayouts(newLayout)
  }

  const onTabChange = (newTabKey: string): void => {
    setCurrentTabKey(newTabKey)
  }

  const onTabRename = (newTabKey: string, newTabLabel: string): void => {
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const newTabIndex: number = _.findIndex(newLayout, {key: newTabKey})
    newLayout[newTabIndex].label = newTabLabel
    setLayouts(newLayout)
  }

  const onTabAdd = (newTabLabel: string): void => {
    if (newTabLabel === 'new') {
      console.log('Can\'t name new tab \'new\'')
      return
    }
    const newKey = '' + new Date().getTime()
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    newLayout.push({
      label: newTabLabel,
      key: newKey,
      body: { lg: [], md: [], sm: [] }
    })
    setCurrentTabKey(newKey)
    setLayouts(newLayout)
  }

  const onTabDelete = (newTabKey: string): void => {
    if (layouts.length <= 1) {
      console.log('wont delete, last dashboard tab')
      return
    }
    const newLayout: LayoutTabs = _.cloneDeep(layouts)
    const newTabIndex: number = _.findIndex(newLayout, {key: newTabKey})

    newLayout.splice(newTabIndex, 1)
    const newCurrentTabKey =  newLayout[0].key
    setCurrentTabKey(newCurrentTabKey)
    setLayouts(newLayout)
  }

  const onTabMove = (sourceKey: string, howMuch: number): void => {
    let newLayout: LayoutTabs = _.cloneDeep(layouts)
    const sourceIndex = _.findIndex(layouts, {key: sourceKey})
    const targetIndex: number = sourceIndex + howMuch
    const targetLayout: LayoutTab = newLayout[targetIndex]
    const sourceLayout: LayoutTab = newLayout[sourceIndex]
    newLayout = newLayout.map((layout, index) => {
      if (index === targetIndex) {
        return sourceLayout
      }
      if (index === sourceIndex) {
        return targetLayout
      }
      return layouts[index]
    })
    const newCurrentTab: LayoutTab = _.find(layouts, {key: currentTabKey}) as LayoutTab
    setCurrentTabKey(newCurrentTab.key)
    setLayouts(newLayout)
  }

  const onBreakpointChange = (breakpoint: Breakpoint): void => {
    setCurrentBreakpoint(breakpoint)
  }

  const onEditModeOn = (): void => {
    setEditMode(true)
    setAddMode(false)
    setBackupLayouts(layouts)
  }

  const onAddChange = (): void => {
    setAddMode(!addMode)
  }

  const onCancelEdit = (): void => {
    setEditMode(false)
    setAddMode(false)
    setLayouts(backupLayouts)
  }

  const onResetEdit = async (): Promise<any> => {
    await DashboardAPI.resetDashboard(id, defaultWidgets, defaultLayouts, defaultConfig)
    initDashboard()
    setAddMode(false)
    setEditMode(false)
  }

  const onSaveEdit = async (): Promise<any> => {
    await DashboardAPI.saveDashboard(id, widgets, layouts, config)
    setAddMode(false)
    setEditMode(false)
  }

  const onWidgetDragStart = (layout: RGLLayout[], oldItem: RGLLayout, newItem: RGLLayout) => {
    setDraggedWidget(newItem)
  }

  const onWidgetDragStop = () => {
    setDraggedWidget(undefined)
  }

  if (!mounted) {
    return (
      <div className='c-dashboard__loading' style={{ paddingTop: '3rem' }}>
        <Loader />
        <span>{_labels.loading}</span>
      </div>
    )
  }

  return (
    <>
      <GlobalStyle />
      <DashboardDiv className={classNames(className, currentBreakpoint)}>
        {configurable && (
          <DashboardControlPanel
            labels={_labels}
            addMode={addMode}
            editMode={editMode}
            highContrast={highContrast}
            onEditModeOn={onEditModeOn}
            onCancelEdit={onCancelEdit}
            onSaveEdit={onSaveEdit}
            onResetEdit={onResetEdit}
            onAddChange={onAddChange}
          />
        )}
        <WidgetAddAreaDiv className={classNames({ open: addMode })}>
          {addMode && (
            <WidgetAddArea
              availableWidgets={availableWidgets}
              highContrast={highContrast}
              labels={_labels}
              myWidgets={myWidgets}
              onPlaceholderWidgetAdd={onPlaceholderWidgetAdd}
            />
          )}
        </WidgetAddAreaDiv>
        <DashboardGrid
          currentTab={currentTabKey}
          draggedWidget={draggedWidget}
          droppingItem={droppingItem}
          editMode={editMode}
          highContrast={highContrast}
          layouts={layouts}
          currentBreakpoint={currentBreakpoint}
          onBreakpointChange={onBreakpointChange}
          onLayoutChange={onLayoutChange}
          onTabAdd={onTabAdd}
          onTabChange={onTabChange}
          onTabDelete={onTabDelete}
          onTabMove={onTabMove}
          onTabRename={onTabRename}
          onWidgetDragStart={onWidgetDragStart}
          onWidgetDragStop={onWidgetDragStop}
          onWidgetDelete={onWidgetDelete}
          onWidgetDrop={onWidgetDrop}
          onWidgetFullFocus={onWidgetFullFocus}
          onWidgetResize={onWidgetResize}
          onWidgetRestoreFocus={onWidgetRestoreFocus}
          onWidgetUpdate={onWidgetUpdate}
          labels={_labels}
          rowHeight={config.rowHeight}
          widgets={widgets}
          myWidgets={myWidgets}
        />
      </DashboardDiv>
    </>
  )
}

Dashboard.propTypes = {
  afterLayoutChange: PT.func,
  allowedWidgets: PT.array,
  defaultConfig: ConfigPropType,
  defaultLayouts: LayoutTabsPropType,
  defaultTab: PT.string,
  defaultWidgets: WidgetsPropType,
  extraWidgets: WidgetMapPropType,
  highContrast: PT.bool,
  id: PT.string.isRequired,
  initialBreakpoint: BreakpointPropType,
  labels: LabelsPropType
}

Dashboard.defaultProps = DashboardConfig
export default Dashboard
